import { escapeHtml } from './ui.js';
import { fillKeys, hasKeys } from './keys.js';
import { hasFeature } from './features.js';
import { canAssets } from './assets-access.js';

// WHAT'S NEW — v4.2.0, rebuilt to Karan's design of 25/10/2026.
//
// It used to be one long scrolling list of every line of every release since
// your last visit. Now it opens on the HIGHLIGHTS — a numbered list on the
// left, the picture, the words and a "Show me how" on the right, moving on by
// itself (hover or focus holds it; reduced motion never moves) — and then the
// SMALLER CHANGES, gathered by area in two columns. "All releases" is the old
// long list, kept as the full history.
//
// It COMBINES every release the person has not seen yet ("Since you were last
// here: 4.0.0, 4.0.1 and 4.1.0, all on Friday."), and every highlight and
// every item still passes exactly the same audience gates as before — roles,
// templates (contractor vs consultancy) and emails. "2 of 4" and "Smaller
// changes 7" are counted AFTER that filtering. Nothing to show → it does not open.
//
// Seen-state is unchanged: app.js keeps the newest version shown in
// localStorage (maybeShowWhatsNew) and passes the unseen entries here.

export const wnVerNum = (v) => (String(v).match(/v[\d.]+/) || [String(v)])[0]; // "Benchmark v1.0.0" → "v1.0.0"
export const itemText = (it) => (typeof it === 'string' ? it : (it && it.text) || '');
// An item may also carry `emails` (Karan 05/10): a build that is one person's
// alone — Calculate, while it is being built — must not be announced to the
// practice. Both gates apply when both are given.
// And `templates` (v4.0.1): a note about a contractor screen is for contractor
// companies only — ASP never hears about Quotes it doesn't have.
// And `features`: every listed organisation feature must be on.
// v4.2.0: the SAME gate decides a highlight.
export function itemVisible(it, roles = [], email = '', template = '') {
  if (typeof it === 'string' || !it) return true;
  if (it.templates && !it.templates.includes(template || 'consultancy')) return false;
  if (it.roles && !it.roles.some((r) => roles.includes(r))) return false;
  if (it.emails && !it.emails.includes(String(email || '').toLowerCase())) return false;
  // And `features` (v4.25.0, Assets): a note about a module is for the companies
  // that have it switched on — every key named must be on (features.js).
  if (Array.isArray(it.features) && !it.features.every((k) => hasFeature(k))) return false;
  // Assets: also the company's own list (settings.assets.access; empty = everyone).
  if (Array.isArray(it.features) && it.features.includes('assets') && !canAssets(email)) return false;
  return true;
}

const bare = (v) => (String(v || '').match(/\d+\.\d+(?:\.\d+)?/) || [String(v || '')])[0];
const dateOf = (d) => { const t = Date.parse(String(d || '').split('·')[0].trim()); return isNaN(t) ? null : new Date(t); };
const AREA_ORDER = ['Home', 'Meeting mode', 'Quotes', 'Tasks', 'Time', 'Projects', 'Everywhere', 'Phone', 'Team', 'Issues'];
const AREA_LAST = ['Fixes', 'Other', 'Behind the scenes'];

/** An item's area: its own, or the "Area:" it starts with, or Fixes/Behind the scenes/Other. Returns [area, text]. */
export function areaOf(it) {
  const t = itemText(it).trim();
  if (it && typeof it !== 'string' && it.area) return [it.area, t];
  if (/^behind the scenes\b/i.test(t)) return ['Behind the scenes', t];
  if (/^(fixed|fix|housekeeping)\b/i.test(t)) { const r = t.replace(/^(fixed|fix|housekeeping)\s*:\s*/i, ''); return ['Fixes', r.charAt(0).toUpperCase() + r.slice(1)]; }
  const m = t.match(/^([A-Z][A-Za-z ’'-]{1,22}):\s+(.+)$/);
  if (m) return [m[1], m[2].charAt(0).toUpperCase() + m[2].slice(1)];
  return ['Other', t];
}

/**
 * What one person is shown from a set of entries (newest first, as in the
 * changelog). Pure — the tests drive it directly.
 *   highlights: oldest release first, in the order written, gated
 *   small:      [{ area, items:[{text, touch, pill}] }] — every visible item a
 *               visible highlight does not already tell
 *   title:      the release title with the most highlights for this person
 *   versions:   the releases combined, oldest first
 */
export function whatsNewFor(entries, { roles = [], email = '', template = '' } = {}) {
  const vis = (x) => itemVisible(x, roles, email, template);
  const list = (entries || []).filter(Boolean);
  const chrono = [...list].reverse();
  const highlights = [];
  let title = '', best = 0;
  for (const e of chrono) {
    const hs = (e.highlights || []).filter(vis).map((h) => ({ ...h, v: e.v }));
    highlights.push(...hs);
    if (e.title && hs.length >= best && hs.length) { best = hs.length; title = e.title; }
  }
  const told = new Set(highlights.map((h) => `${h.v}#${h.id}`));
  const groups = new Map();
  let count = 0;
  for (const e of chrono) {
    for (const it of (e.items || [])) {
      if (!vis(it)) continue;
      if (it && typeof it !== 'string' && it.highlight && told.has(`${e.v}#${it.highlight}`)) continue;
      const [area, text] = areaOf(it);
      const pill = typeof it !== 'string' && it && (it.emails ? 'Only you' : it.roles && !it.templates ? `${it.roles.join(', ')} only` : '');
      if (!groups.has(area)) groups.set(area, []);
      groups.get(area).push({ text, touch: (it && it.touch) || null, pill: pill || '' });
      count++;
    }
  }
  const rank = (a) => (AREA_LAST.includes(a) ? 100 + AREA_LAST.indexOf(a) : AREA_ORDER.includes(a) ? AREA_ORDER.indexOf(a) : 50);
  const small = [...groups.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0])).map(([area, items]) => ({ area, items }));
  const shown = chrono.filter((e) => (e.items || []).some(vis) || (e.highlights || []).some(vis));
  return { highlights, small, smallCount: count, title, versions: shown.map((e) => e.v), entries: shown };
}

/** "Since you were last here: 4.0.0, 4.0.1 and 4.1.0, all on Friday." — '' for a single release. */
export function sinceLine(entries, now = new Date()) {
  if (!entries || entries.length < 2) return '';
  const vs = entries.map((e) => bare(e.v));
  const names = vs.length > 1 ? `${vs.slice(0, -1).join(', ')} and ${vs[vs.length - 1]}` : vs[0];
  const ds = entries.map((e) => dateOf(e.date)).filter(Boolean);
  const same = ds.length === entries.length && ds.every((d) => d.toDateString() === ds[0].toDateString());
  let when = '';
  const recentAll = ds.length === entries.length && ds.every((d) => now >= d && (now - d) < 6 * 864e5);
  if (!same && recentAll) {
    const day = (d) => d.toLocaleDateString('en-GB', { weekday: 'long' });
    when = `, between ${day(ds[0])} and ${day(ds[ds.length - 1])}`;
  }
  if (same) {
    const recent = (now - ds[0]) < 6 * 864e5 && now >= ds[0];
    when = `, all on ${recent ? ds[0].toLocaleDateString('en-GB', { weekday: 'long' }) : ds[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}`;
  }
  return `Since you were last here: ${names}${when}.`;
}

/** Only our own pictures: /assets/… png or webp. Anything else draws no frame. */
export function safeAsset(p) {
  return /^\/assets\/[a-z0-9/_-]+\.(webp|png)$/i.test(String(p || '')) ? String(p) : '';
}

const PLAY = '<svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M3 1.8v8.4L10 6z" fill="currentColor"/></svg>';
const CHEV = '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="M4.5 2.5 8 6l-3.5 3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CHEV_L = '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="M7.5 2.5 4 6l3.5 3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ADVANCE_MS = 9000;

/** The full history — the old splash's list, every release this person may read. */
function allReleasesHtml(entries, vis) {
  return entries.map((e) => {
    const items = (e.items || []).filter(vis);
    if (!items.length) return '';
    return `<section class="wn-rel"><h4 class="wn-rel-h"><span class="wn-mono">${escapeHtml(wnVerNum(e.v))}</span>${e.date ? `<span class="wn-rel-d">${escapeHtml(e.date)}</span>` : ''}</h4>
      <ul class="wn-rel-list">${items.map((it) => `<li>${fillKeys(escapeHtml(itemText(it)), { touch: typeof it === 'string' ? null : it.touch })}</li>`).join('')}</ul></section>`;
  }).filter(Boolean).join('');
}

/**
 * Open the dialog.
 *   entries   the unseen changelog entries (newest first)
 *   all       the whole changelog, for "All releases" (defaults to entries)
 *   build     "Track v4.2.0"
 *   walkthrough(id) → { secs, label, open() } | null   — "Show me how", only when this person may watch it
 *   onOpen(view)                                       — the highlight's "Open …" button
 *   onClose()
 */
export function showWhatsNewSplash(entries, { build = '', roles = [], email = '', template = '', all = null, walkthrough = null, onOpen = null, onClose = null, reducedMotion = null } = {}) {
  const vis = (x) => itemVisible(x, roles, email, template);
  const wn = whatsNewFor(entries, { roles, email, template });
  if (!wn.highlights.length && !wn.smallCount) return null; // nothing relevant to this person
  const H = wn.highlights;
  const newest = wn.entries[wn.entries.length - 1] || {};
  const nd = dateOf(newest.date);
  const verLine = `Track ${bare(newest.v || build).split('.').slice(0, 2).join('.')}${nd ? ` · ${nd.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}` : ''}`;
  const title = wn.title || `Track ${bare(newest.v || build).split('.').slice(0, 2).join('.')}`;
  const since = sinceLine(wn.entries);
  const reduce = reducedMotion != null ? reducedMotion : (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const keys = hasKeys();
  const wts = H.map((h) => (h.help && walkthrough ? walkthrough(h.help) : null));

  let view = H.length ? 'hl' : 'small';
  let i = 0;
  let auto = !reduce && H.length > 1;
  let held = false;
  let timer = null;
  let started = 0;
  let left = ADVANCE_MS;

  const before = document.activeElement;
  const scrim = document.createElement('div');
  scrim.className = 'wn-scrim whatsnew-overlay';
  scrim.innerHTML = `
    <div class="wn" role="dialog" aria-modal="true" aria-labelledby="wn-title" aria-describedby="wn-verline">
      <aside class="wn-side">
        <div class="wn-eyebrow"><span class="wn-dot" aria-hidden="true"></span>What’s new</div>
        <h2 class="wn-title" id="wn-title">${escapeHtml(title)}</h2>
        <div class="wn-ver" id="wn-verline">${escapeHtml(verLine)}</div>
        ${H.length ? `<ol class="wn-hls">${H.map((h, k) => `<li><button type="button" class="wn-hl" data-hl="${k}">
          <span class="wn-n" aria-hidden="true">${k + 1}</span>
          <span class="wn-hl-t"><span class="wn-hl-title">${escapeHtml(h.title)}</span>
          <span class="wn-hl-sub">${!keys && h.subTouch ? escapeHtml(h.subTouch) : fillKeys(escapeHtml(h.sub || ''))}</span></span>
          <span class="wn-prog" aria-hidden="true"><i></i></span></button></li>`).join('')}</ol>` : ''}
        <div class="wn-side-foot">
          ${wn.smallCount ? `<button type="button" class="wn-smallrow" data-small><span>Smaller changes <span class="wn-count">${wn.smallCount}</span></span>${CHEV}</button>` : ''}
          ${since ? `<p class="wn-since">${escapeHtml(since)}</p>` : ''}
        </div>
      </aside>
      <section class="wn-main" aria-live="polite"></section>
      <button type="button" class="wn-x" aria-label="Close what’s new">✕</button>
      <footer class="wn-foot">
        <div class="wn-foot-l"></div>
        <button type="button" class="wn-link" data-all>All releases</button>
        <div class="wn-foot-btns">
          <button type="button" class="wn-btn" data-back>${CHEV_L}<span>Back</span></button>
          <button type="button" class="wn-btn wn-primary" data-next><span>Next</span>${CHEV}</button>
        </div>
        <button type="button" class="wn-link wn-phone-small" data-small2 ${wn.smallCount ? '' : 'hidden'}>Smaller changes · ${wn.smallCount}</button>
      </footer>
    </div>`;
  const card = scrim.querySelector('.wn');
  const main = scrim.querySelector('.wn-main');
  const footL = scrim.querySelector('.wn-foot-l');
  const back = scrim.querySelector('[data-back]');
  const next = scrim.querySelector('[data-next]');
  const allBtn = scrim.querySelector('[data-all]');

  const narrow = () => typeof matchMedia === 'function' && matchMedia('(max-width: 760px)').matches;
  const setNext = (label) => { next.querySelector('span').textContent = label; next.classList.toggle('wn-done', label === 'Got it'); };
  const stopTimer = () => { if (timer) { clearTimeout(timer); timer = null; } };
  const arm = () => {
    stopTimer();
    if (!auto || view !== 'hl' || held) return;
    started = Date.now();
    timer = setTimeout(() => { left = ADVANCE_MS; go(i + 1 < H.length ? { hl: i + 1 } : { v: 'small' }, true); }, left);
  };
  const hold = (on) => {
    if (!auto || held === on) return;
    held = on;
    card.classList.toggle('wn-held', on);
    if (on) { left = Math.max(400, left - (Date.now() - started)); stopTimer(); } else arm();
  };

  const draw = () => {
    card.dataset.view = view;
    card.classList.toggle('wn-auto', auto && !reduce);
    scrim.querySelectorAll('.wn-hl').forEach((b, k) => {
      const on = view === 'hl' && k === i;
      b.classList.toggle('on', on);
      b.setAttribute('aria-current', on ? 'step' : 'false');
      const bar = b.querySelector('.wn-prog i');
      bar.style.animation = 'none';
      if (on) { void bar.offsetWidth; bar.style.animation = ''; }
    });
    scrim.querySelector('[data-small]')?.classList.toggle('on', view === 'small');
    if (view === 'hl') {
      const h = H[i];
      const img = safeAsset(h.image);
      const wt = wts[i];
      main.innerHTML = `
        <div class="wn-phone-eyebrow"><span class="wn-dot" aria-hidden="true"></span>What’s new · ${escapeHtml(title)}</div>
        ${img ? `<figure class="wn-pic"><img src="${escapeHtml(img)}" alt="${escapeHtml(h.title)}" decoding="async"></figure>` : ''}
        <div class="wn-body${img ? '' : ' wn-noimg'}">
          ${img ? '' : `<span class="wn-big-n" aria-hidden="true">${i + 1}</span>`}
          <h3 class="wn-h">${escapeHtml(h.title)}${h.audience ? ` <span class="wn-aud">${escapeHtml(h.audience)}</span>` : ''}</h3>
          <p class="wn-p">${fillKeys(escapeHtml(h.text || ''), { touch: h.touch })}</p>
          ${wt || (h.open && onOpen) ? `<div class="wn-acts">
            ${wt ? `<button type="button" class="wn-btn" data-how>${PLAY}<span>Show me how · ${escapeHtml(wt.label)}</span></button>` : ''}
            ${h.open && onOpen ? `<button type="button" class="wn-btn" data-open>${escapeHtml(h.open.label)}</button>` : ''}
          </div>` : ''}
          <div class="wn-dots" aria-hidden="true">${H.map((_, k) => `<i class="${k === i ? 'on' : ''}"></i>`).join('')}</div>
        </div>`;
      footL.innerHTML = `<span>${i + 1} of ${H.length}</span>${keys ? '<span class="wn-keys"><kbd>←</kbd><kbd>→</kbd> to move</span>' : ''}`;
      const first = i === 0;
      back.hidden = false;
      back.querySelector('span').textContent = first ? 'Skip' : 'Back';
      back.classList.toggle('wn-skip', first);
      const lastHl = i === H.length - 1;
      setNext(lastHl && (!wn.smallCount || narrow()) ? 'Got it' : 'Next');
      // A phone screenshot is tall: show it whole on the grey ground rather than cropping it.
      const pic = main.querySelector('.wn-pic img');
      const fit = () => { if (pic.naturalHeight > pic.naturalWidth * 0.8) pic.parentNode.classList.add('wn-tall'); };
      if (pic) { if (pic.complete && pic.naturalWidth) fit(); else pic.addEventListener('load', fit, { once: true }); }
      main.querySelector('[data-how]')?.addEventListener('click', () => { close(); wt.open(); });
      main.querySelector('[data-open]')?.addEventListener('click', () => { close(); onOpen(h.open.view); });
    } else if (view === 'small') {
      main.innerHTML = `
        <div class="wn-phone-eyebrow"><span class="wn-dot" aria-hidden="true"></span>What’s new · ${escapeHtml(title)}</div>
        <h3 class="wn-h wn-small-h">Smaller changes</h3>
        <p class="wn-lede">Worth knowing, nothing you need to learn.</p>
        <div class="wn-areas">${wn.small.map((g) => `<section class="wn-area"><h4>${escapeHtml(g.area)}</h4>
          ${g.items.map((it) => `<p class="wn-item">${fillKeys(escapeHtml(it.text), { touch: it.touch })}${it.pill ? ` <span class="wn-aud">${escapeHtml(it.pill)}</span>` : ''}</p>`).join('')}</section>`).join('')}</div>`;
      footL.innerHTML = '<span>That’s everything in this release</span>';
      back.hidden = true;   // the design: one button, Got it (← and the list still go back)
      setNext('Got it');
    } else {
      main.innerHTML = `<h3 class="wn-h wn-small-h">All releases</h3>
        <p class="wn-lede">You’re on <span class="wn-mono">${escapeHtml(build)}</span>. Every release, newest first, in the words it shipped with.</p>
        <div class="wn-rels">${allReleasesHtml(all || entries, vis)}</div>`;
      footL.innerHTML = '<span>Every release, newest first</span>';
      back.hidden = false;
      back.querySelector('span').textContent = 'Back';
      back.classList.remove('wn-skip');
      setNext('Got it');
    }
    allBtn.hidden = view === 'all';
    main.scrollTop = 0;
    arm();
  };
  let lastView = 'hl';
  function go(to, byTimer = false) {
    if (!byTimer) { auto = false; stopTimer(); }
    left = ADVANCE_MS;
    if (to.hl != null) { view = 'hl'; i = Math.max(0, Math.min(H.length - 1, to.hl)); } else { if (to.v === 'all') lastView = view; view = to.v; }
    draw();
  }
  const forward = () => {
    // On a phone the last highlight's button is Got it; Smaller changes is the link under it (design p5).
    if (view === 'hl') { if (i + 1 < H.length) go({ hl: i + 1 }); else if (wn.smallCount && !narrow()) go({ v: 'small' }); else close(); } else close();
  };
  const backward = () => {
    if (view === 'hl') { if (i > 0) go({ hl: i - 1 }); else if (wn.smallCount) go({ v: 'small' }); else close(); } // first highlight: Skip
    else if (view === 'small') { if (H.length) go({ hl: H.length - 1 }); }
    else go(lastView === 'small' ? { v: 'small' } : H.length ? { hl: i } : { v: 'small' });
  };

  let closed = false;
  function close() {
    if (closed) return;
    closed = true;
    stopTimer();
    document.removeEventListener('keydown', onKey, true);
    scrim.remove();
    if (before && typeof before.focus === 'function' && document.contains(before)) before.focus();
    if (onClose) onClose();
  }
  const focusables = () => [...card.querySelectorAll('button:not([hidden]), [href], [tabindex]:not([tabindex="-1"])')].filter((x) => !x.disabled && x.offsetParent !== null);
  function onKey(ev) {
    if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); close(); return; }
    if (ev.key === 'Tab') {
      const f = focusables(); if (!f.length) return;
      const k = f.indexOf(document.activeElement);
      if (ev.shiftKey && (k <= 0)) { ev.preventDefault(); f[f.length - 1].focus(); } else if (!ev.shiftKey && k === f.length - 1) { ev.preventDefault(); f[0].focus(); } else if (k < 0) { ev.preventDefault(); f[0].focus(); }
      return;
    }
    if (ev.target && /^(INPUT|TEXTAREA|SELECT)$/.test(ev.target.tagName)) return;
    if (ev.key === 'ArrowRight') { ev.preventDefault(); if (view === 'hl') forward(); }
    else if (ev.key === 'ArrowLeft') { ev.preventDefault(); if (view === 'hl' && i > 0) go({ hl: i - 1 }); else if (view === 'small' && H.length) go({ hl: H.length - 1 }); }
  }

  scrim.querySelectorAll('.wn-hl').forEach((b) => b.addEventListener('click', () => go({ hl: Number(b.dataset.hl) })));
  scrim.querySelector('[data-small]')?.addEventListener('click', () => go({ v: 'small' }));
  scrim.querySelector('[data-small2]')?.addEventListener('click', () => go({ v: 'small' }));
  allBtn.addEventListener('click', () => go({ v: 'all' }));
  scrim.querySelector('.wn-x').addEventListener('click', close);
  next.addEventListener('click', forward);
  back.addEventListener('click', backward);
  scrim.addEventListener('click', (ev) => { if (ev.target === scrim) close(); });
  card.addEventListener('mouseenter', () => hold(true));
  card.addEventListener('mouseleave', () => { if (!card.contains(document.activeElement) || document.activeElement === card) hold(false); });
  card.addEventListener('focusin', (ev) => { if (ev.target !== card) hold(true); });
  card.addEventListener('focusout', (ev) => { if (!card.contains(ev.relatedTarget) && !card.matches(':hover')) hold(false); });
  // Phone: swipe the sheet left and right.
  let sx = null, sy = 0;
  main.addEventListener('touchstart', (ev) => { const t = ev.touches[0]; sx = t.clientX; sy = t.clientY; }, { passive: true });
  main.addEventListener('touchend', (ev) => {
    if (sx == null || view !== 'hl') return;
    const t = ev.changedTouches[0]; const dx = t.clientX - sx, dy = t.clientY - sy; sx = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) { if (dx < 0) forward(); else if (i > 0) go({ hl: i - 1 }); }
  }, { passive: true });

  card.style.setProperty('--wn-dur', `${ADVANCE_MS}ms`);
  document.body.appendChild(scrim);
  document.addEventListener('keydown', onKey, true);
  draw();
  // Focus the dialog itself, so a keyboard user starts inside it without holding the auto-advance.
  card.setAttribute('tabindex', '-1');
  card.focus({ preventScroll: true });
  return { close, el: scrim };
}
